<template>
  <validation-observer v-slot="{ handleSubmit, meta: { valid } }" ref="observer">
    <ld-modal-content private-header :title="modalTitle" :subtitle="modalSubtitle" :class="{'inline-modal': isInline}">
      <template #header-right-buttons>
        <b-tooltip v-if="nurseCallFailure?.message" :label="nurseCallFailure.message"
          type="is-dark" position="is-left" multilined>
          <i class="mdi mdi-information-outline nurse-call-failure__info"></i>
        </b-tooltip>
      </template>
      <div v-if="isAlarm">
        <template v-if="nurseCallFailure">
          <div class="nurse-call-failure">
            <div class="nurse-call-failure__statuses">
              <p class="nurse-call-failure__line">Alarm created in LookDeep <i class="mdi mdi-check-circle nurse-call-failure__icon--success"></i></p>
              <p class="nurse-call-failure__line">Nurse call not sent to the room <i class="mdi mdi-close-circle nurse-call-failure__icon--failure"></i></p>
            </div>
            <p class="nurse-call-failure__detail" v-if="nurseCallFailure.retryable">You can retry the nurse call, but it is likely down. Alert the bedside team directly.</p>
            <p class="nurse-call-failure__detail" v-else>Alert the bedside team directly.</p>
          </div>
        </template>
        <template v-else-if="downtimeWarning">
          <div class="downtime-warning">
            <p class="downtime-warning__title">Nurse Call System is down - Please call to alert bedside team</p>
            <p class="downtime-warning__detail">Reported {{ $helpers.formatDate(downtimeWarning.reported_at) }}</p>
          </div>
        </template>
        <template v-else>
          <p class="alarm-confirmation" v-if="monitor">{{ confirmationMessage }}</p>
        </template>
      </div>
      <div v-else>
        <input-with-validation :field-label="eventTypeLabel" rules="required" ref="eventTypeField">
          <template #custom="{ handleChange }">
            <div class="field-body event-type">
              <b-radio-button type="is-prevented" v-model="alarm.event_type" @update:modelValue="handleChange" native-value="prevented">
                <div class="event-type__content">
                  <span>Adverse Event</span>
                  <span>Prevented</span>
                </div>
              </b-radio-button>
              <b-radio-button type="is-occurred" v-model="alarm.event_type" @update:modelValue="handleChange" native-value="occurred">
                <div class="event-type__content">
                  <span>Adverse Event</span>
                  <span>Occurred</span>
                </div>
              </b-radio-button>
              <b-radio-button type="is-note" v-model="alarm.event_type" @update:modelValue="handleChange" native-value="note">
                <div class="event-type__content">
                  <span>&nbsp;</span>
                  <span>Note</span>
                </div>
              </b-radio-button>
            </div>
          </template>
        </input-with-validation>
        <input-with-validation v-if="alarm.alarm_type != 'alarm'" :disabled="!!existingData" class="event-datepicker" rules="required" field-label="Time of Event" b-name="datetimepicker" v-model="alarm.observed_at"></input-with-validation>
        <input-with-validation v-if="alarm.event_type != 'note'" rules="required" field-label="Category" b-name="select" v-model="alarm.stat_alarm_category_id" expanded>
          <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.label }}</option>
        </input-with-validation>
        <input-with-validation field-label="Description" b-name="input" :rules="noteValidationRule" type="textarea" v-model="alarm.description"></input-with-validation>
      </div>
      <template #footer>
        <template v-if="isAlarm">
          <template v-if="nurseCallFailure">
            <button class="button is-text" @click="$emit('close')">Close</button>
            <button v-if="nurseCallFailure.retryable" class="button is-primary" @click="retryNurseCall" :disabled="retrying">Retry Nurse Call</button>
          </template>
          <template v-else-if="downtimeWarning">
            <button class="button is-primary" @click="$emit('close')">OK</button>
          </template>
          <template v-else>
            <button class="button is-text" @click="$emit('close');">Cancel</button>
            <button class="button is-primary" @click="confirmAlarm" :disabled="confirming">Confirm</button>
          </template>
        </template>
        <template v-else>
          <button class="button is-text" v-if="!isAlarm" @click="$emit('close');">Cancel</button>
          <button class="button is-primary" @click="handleSubmit(save)" :disabled="!valid">Submit</button>
        </template>
      </template>
    </ld-modal-content>
  </validation-observer>
</template>

<script>
import LdModalContent from "@/components/LdModalContent";
import InputWithValidation from "@/components/validation/InputWithValidation";
import to from "@/lib/to";
import ValidationObserver from "@/components/validation/ValidationObserver";

export default {
name: "Alarm",
  props: {
    monitor_id: { type: Number, required: true },
    isAlarm: { type: Boolean, required: false, default: false },
    isInline: { type: Boolean, default: false },
    existingData: { type: Object, default: null }
  },
  components: {ValidationObserver, InputWithValidation, LdModalContent},
  watch: {
    'monitor_id': {
      async handler(newMonitorId) {
        if (newMonitorId && !this.isInline) {
          const [err, response] = await to(this.$http.get(`/patient_monitors/${newMonitorId}`))
          if (!err) {
            this.monitor = response.data
          }
        }
      },
      immediate: true
    }
  },
  computed: {
    eventTypeLabel() {
      return 'Event Type'
    },
    noteValidationRule() {
      return this.alarm.event_type == 'note' ? 'required' : ''
    },
    modalTitle() {
      if (this.isAlarm) {
        if (this.nurseCallFailure) return 'Nurse Call Integration Error'
        return this.downtimeWarning ? 'Warning' : 'Confirmation'
      } else {
        return this.alarm.alarm_type == 'alarm' ? 'Alarm' : 'Event'
      }
    },
    modalSubtitle() {
      if (this.monitor) {
        return `${this.$helpers.mask('patient_name', this.monitor.patient.display_name)} / ${this.monitor.room?.name}`
      }
      return false
    },
    alarmType() {
      return this.isAlarm ? 'alarm' : 'event'
    },
    confirmationMessage() {
      return this.monitor?.division.alarm_confirm_message || this.$store.state.currentHospital.alarm_confirm_message || 'Confirm Alarm Activation'
    }
  },
  methods: {
    async save() {
      if (this.existingData) {
        this.updateAlarm()
      } else {
        const [err, ] = await to(this.http.post('/stat_alarms', {
          patient_monitor_id: this.monitor_id,
          ...this.alarm,
          alarm_type: 'event',
          source: this.$helpers.getMonitoringSource()
        }))
        if (!err) {
          this.$buefy.toast.open({
            message: 'Event successfully created',
            type: 'is-success'
          });
          this.$emit('close');
          this.$emit('save');
        }
      }
    },
    async confirmAlarm() {
      if (this.confirming) return
      this.confirming = true
      const [err, response] = await to(this.http.post('/stat_alarms', {
        patient_monitor_id: this.monitor_id,
        alarm_type: 'alarm',
        source: this.$helpers.getMonitoringSource()
      }))
      if (err) {
        this.confirming = false
        return
      }
      if (!err) {
        this.$emit('save')
        this.$emit('alarm-issued', { patient_monitor_id: this.monitor_id })

        if (response.data.nurse_call?.status === 'failed') {
          this.createdAlarmId = response.data.id
          this.nurseCallFailure = response.data.nurse_call
          return
        }

        if (this.monitor?.room_id) {
          const [dtErr, dtResp] = await to(this.$http.get('/nurse_call_downtime_reports/check_downtime', {
            params: { room_id: this.monitor.room_id || this.monitor?.room?.id },
            skipLoading: true,
            skipDivisions: true
          }))
          if (!dtErr && dtResp.data.downtime) {
            this.downtimeWarning = dtResp.data
            return
          }
        }

        this.$buefy.toast.open({
          message: 'Alarm event has been successfully activated.',
          type: 'is-success'
        })
        this.$emit('close')
      }
    },
    async retryNurseCall() {
      if (this.retrying) return
      this.retrying = true
      const [err, response] = await to(this.$http.post(`/stat_alarms/${this.createdAlarmId}/retry_nurse_call`, {}, { handleErrors: true }))
      this.retrying = false
      if (!err && response.data.nurse_call.status === 'sent') {
        this.$buefy.toast.open({
          message: 'Nurse call sent.',
          type: 'is-success'
        })
        this.$emit('close')
        return
      }
      this.nurseCallFailure = err
        ? { retryable: false, message: err.response?.data?.error || 'Retry failed' }
        : response.data.nurse_call
    },
    async updateAlarm() {
      const [err,] = await to(this.http.put(`/stat_alarms/${this.alarm.id}`, this.alarm))
      if (!err) {
        this.$buefy.toast.open({
          message: 'Event details has been updated',
          type: 'is-success'
        });
        this.$emit('close');
        this.$emit('save');
      }
    }
  },
  async beforeMount() {
    const [err, response] = await to(this.$http.get('/stat_alarms/categories'))
    if (!err) {
      this.categories = response.data
    }
  },
  mounted() {
    if (this.$refs.eventTypeField) {
      this.$refs.eventTypeField.$refs.field.handleChange(this.alarm.event_type);
    }
    if (this.existingData) {
      this.alarm = this.existingData.payload
      this.alarm.observed_at = this.alarm.observed_at && new Date(Date.parse(this.alarm.observed_at))
    }
  },
  data() {
    return {
      confirming: false,
      alarmConfirmed: false,
      categories: [],
      savedAlarm: null,
      alarm: {
        event_type: 'prevented',
        stat_alarm_category_id: null,
        description: '',
        observed_at: new Date()
      },
      monitor: null,
      downtimeWarning: null,
      nurseCallFailure: null,
      createdAlarmId: null,
      retrying: false
    }
  }
}
</script>

<style scoped lang="scss">
:deep() {
  .event-type .control:not(:last-child) {
    margin-right: 10px;
  }
  .event-type {
    .control .b-radio {
      height: auto;
    }
    .event-type__content {
      padding: 10px;
      display: flex;
      flex-direction: column;
      span:last-child {
        padding-top: 2px;
        font-weight: 500;
      }
    }
  }
  .ld-modal__footer {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 20px;
  }
  .field-body .control {
    flex: 1;
  }
}
.alarm-success {
  margin-bottom: 20px;
}
.alarm-confirmation {
  text-align: center;
  margin: 20px 0 15px;
}
.downtime-warning {
  text-align: center;
  margin: 20px 0 15px;
  &__title {
    font-size: 16px;
    font-weight: 500;
    line-height: 1.3;
    color: var(--ld-status-danger);
    margin-bottom: 8px;
  }
  &__detail {
    font-size: 14px;
    color: #666;
  }
}
.nurse-call-failure {
  text-align: center;
  margin: 0 0 15px;
  &__statuses {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    margin-bottom: 16px;
  }
  &__line {
    font-size: 16px;
    font-weight: 500;
    line-height: 1.4;
    display: flex;
    align-items: center;
    .mdi {
      font-size: 18px;
      margin-left: 8px;
    }
  }
  &__icon--success {
    color: var(--ld-status-success);
  }
  &__icon--failure {
    color: var(--ld-status-danger);
  }
  &__detail {
    font-size: 14px;
    max-width: 400px;
    margin: 0 auto;
  }
}
.nurse-call-failure__info {
  font-size: 18px;
  color: #666;
  cursor: help;
}
.inline-modal {
  width: 100%;
  max-width: 100%;
  overflow: auto;
  max-height: none;
  .event-type {
    display: flex;
    flex-direction: column;
    .control {
      margin: 0 0 10px 0;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
  .event-type__content {
    padding: 0;
  }
}
</style>
